/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {logWarning} from "App/debug";
import IDataStorage from "App/storage/data_storage_interface";
import {DataResourceObserver} from "App/storage/data_resource_observer";
import {getDOMInlinedDataElement} from "App/storage/utils";
import DynamicAssetsManager from "App/storage/dynamic_assets_manager";

export enum DataResourceState {
    QUEUED,
    DOWNLOADING,
    DOWNLOADED,
    ABORTED,
    FAILED
}

export class DataResource {
    private _dataStorage: IDataStorage;
    private _observers: DataResourceObserver[] = [];
    private _properties: {
        url: string,
        binSize: number,
        priority: number,
        required: boolean,
        localBase64Data: string
        localData: any
    };
    private _state: DataResourceState = DataResourceState.QUEUED;
    private _xhr: XMLHttpRequest | any = null;

    constructor(aDataStorage: IDataStorage) {
        this._dataStorage = aDataStorage;
        this._properties = {
            url: "",
            binSize: 0,
            priority: 0,
            required: false,
            localBase64Data: "",
            localData: ""
        };
    }

    public getState(): DataResourceState
    {
        return this._state;
    }

    public setProperties(aProperties: {}): void
    {
        // merge caller properties to the resources
        for (let property in aProperties) {
            this._properties[property] = aProperties[property];
        }
    }

    public attachObserver(aObserver: DataResourceObserver): void
    {
        this._observers.push(aObserver);

        aObserver.attachToDataResource(this);
    }

    public detachObserver(aObserver: DataResourceObserver): void
    {
        let observerItemIndex = this._observers.indexOf(aObserver);
        if (observerItemIndex !== -1) {
            this._observers.slice(observerItemIndex, 1);
        }

        // remove resource reference from observer
        aObserver.detachFromDataResource(this);
    }

    public detachAllObservers(): void
    {
        for (let observer of this._observers) {
            observer.detachFromDataResource(this);
        }
    }

    public hasObservers(): boolean
    {
        return this._observers.length > 0;
    }

    public getUrl(): string
    {
        return this._properties.url;
    }

    public getPriority(): number
    {
        return this._properties.priority;
    }

    public getBinarySize(): number
    {
        if (this._properties.binSize === 0) {
            // we probably do not know the size of the requested data (e.g. remote textures for terrain, skybox etc)
            // so set the bin size to some small value in order to keep the required data to be downloaded before
            // the model loaded event will be dispatched
            return 1;
        }

        return this._properties.binSize;
    }

    public getSAPIModelUID(): number
    {
        for (let observer of this._observers) {
            return observer.getModel().getSceneAPIModelUID();
        }

        return -1;
    }

    public isRequired(): boolean
    {
        return this._properties.required;
    }

    /**
     * Return true when the url contains "localData" hash (i.e. the data are defined inside the observer directly) or we
     * find an element in DOM with the name defined by the url property which ahs defined base64data attribute
     */
    public getIsLocal(): boolean
    {
        if (this._properties.url.indexOf("localData") !== -1) {
            return true;
        }

        let base64data = this.getLocalBase64Data();
        return base64data !== "";
    }

    public getLocalBase64Data(): string
    {
        let dataElement = getDOMInlinedDataElement(this._properties.url);
        if (dataElement) {
            let base64Data  = dataElement.getAttribute('base64data');
            if (base64Data) {
                return base64Data;
            }
        }

        return this._properties.localBase64Data !== undefined ? this._properties.localBase64Data : "";
    }

    /***
     * Request generated by download priority worker to download data specfied in the aQuery.
     * @param aQuery Data download requests description
     */
    public download(aWorkerMessage): void
    {
        let onload = (): void => {
            if (this._xhr.status === 200) {
                // free slots
                this._dataStorage.getDownloadManager().returnSlotsDelayed(aWorkerMessage);

                this.dataDownloaded(this._xhr.response, this._xhr.status);
                this._dataStorage.resourceDownloaded(this);
                this._xhr = null;
            }
        };

        let onerror = (): void => {
            this._dataStorage.getDownloadManager().returnSlots(aWorkerMessage);

            this.dataDownloadFailed(this._xhr.response, this._xhr.status);
            this._dataStorage.resourceDownloadFailed(this);
        };

        let onabort = (): void => {
            this._dataStorage.getDownloadManager().returnSlots(aWorkerMessage);

            this.dataDownloadAborted(this._xhr.response, this._xhr.status);
            this._dataStorage.resourceDownloadAborted(this);
        };

        this._state = DataResourceState.DOWNLOADING;

        if (aWorkerMessage.uri.indexOf("@asset") !== -1) {
            DynamicAssetsManager.loadAsset(aWorkerMessage.uri, (asset: any) => {
                this._xhr = new XMLHttpRequest();
                this._xhr.addEventListener('load', onload);
                this._xhr.addEventListener('error', onerror);
                this._xhr.addEventListener('abort', onabort);

                let content =  asset.default ? asset.default : asset;
                if (content.indexOf("data:") !== -1) { // in url data
                    this._xhr.open('GET', content, true);
                }
                else {
                    this._xhr.open('GET', content.replace(/\\/g, "/"), true);
                }
                this._xhr.responseType = 'arraybuffer';
                this._xhr.send(null);
            });
        }
        else if (this.getIsLocal()) {
            let data = aWorkerMessage.decodedBase64Data;
            if (data === null || data.length === 0) {
                data = this._properties.localData || aWorkerMessage.decodedBase64Data;
            }

            // fake the xml http request is done
            // @ts-ignore
            this._xhr = {
                readyState: XMLHttpRequest.DONE,
                status: 200,
                response: data,
                abort: () => { /**/ }
            };

            onload();
        }
        else {
            this._xhr = new XMLHttpRequest();
            this._xhr.addEventListener('load', onload);
            this._xhr.addEventListener('error', onerror);
            this._xhr.addEventListener('abort', onabort);
            this._xhr.open('GET', aWorkerMessage.uri, true);
            this._xhr.responseType = 'arraybuffer';
            this._xhr.send(null);
        }

        return null;
    }

    public abortDownload(): boolean
    {
        this._state = DataResourceState.ABORTED;

        if (this._xhr.status !== 200) {
            this._xhr.abort();
            return true;
        }

        return false;
    }

    public dataDownloaded(aResponse, aStatus): void
    {
        this._state = DataResourceState.DOWNLOADED;

        for (let observer of this._observers) {
            observer.dataDownloaded(aResponse, this);
        }
    }

    public dataDownloadFailed(aResponse, aStatus): void
    {
        this._state = DataResourceState.FAILED;

        logWarning("Cannot download resource: " + this._properties.url + ". Network status: " + aStatus);

        for (let observer of this._observers) {
            observer.dataDownloadFailed(aStatus, this);
        }
    }

    public dataDownloadAborted(aResponse, aStatus): void
    {
        this._state = DataResourceState.ABORTED;

        for (let observer of this._observers) {
            observer.dataDownloadAborted(aStatus, this);
        }
    }
}
